import React, { useState } from "react";
import {
  Paper,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableContainer,
  TableBody,
  TableCell,
  Button,
  Checkbox,
  Box,
  FormHelperText,
} from "@material-ui/core";
import { Search as SearchIcon } from "@material-ui/icons";
import { Formik } from "formik";
import moment from "moment/moment";
import api from "../../../api";
import { Pagination } from "@material-ui/lab";
import { CDatePicker } from "../components/CDatePicker";
import { CTextField } from "../components/CTextField";
import ConfirmDialog from "../../common/confirmDialog/ConfirmDialog";
import { CSearchSelect } from "../components/CSearchSelect";
import { SaleStatusEnum } from "../../../enums/enums";

export const Sales = ({ setDataToast, setIsLoading }) => {
  const [filters, setFilters] = useState({
    seller: undefined,
    saleNumber: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    pageSize: 10,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [quantity, setQuantity] = useState(0);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [data, setData] = useState();

  const toastNotification = (type, message) => {
    setDataToast({
      active: true,
      message: message,
      type: type,
    });
  };

  const handleChangePage = (_, newPage) => {
    setCurrentPage(newPage);
    handleSubmit(filters, newPage);
  };

  const handleSelectAll = () => {
    const value = !selectAll;
    setSelectAll(value);
    if (value) {
      setSelectedItems(data?.map(({ id }) => id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSubmit = (values, page) => {
    setIsLoading(true);

    if (!page) {
      setFilters(values);
      setCurrentPage(1);
    }

    api
      .getSales({
        tamanioPagina: values?.pageSize,
        pagina: page || 1,
        productorId: values?.seller?.value,
        fechaDesde: values?.dateFrom,
        fechaHasta: values?.dateTo,
        estado: SaleStatusEnum.Confirmed.Id,
      })
      .then(({ data }) => {
        setQuantity(data.cantidad);
        setData(data.resultado);
      })
      .catch(({ response }) => {
        toastNotification("error", response.data.errores[0]);
        setData([]);
      })
      .finally(() => setIsLoading(false));
  };

  const [confirmDialog, setConfirmDialog] = useState(false);

  const closePreConfirmDialog = () => {
    setConfirmDialog(false);
  };

  const confirmAgreements = () => {
    const salesToProcess = data
      .filter(({ id }) => selectedItems.includes(id))
      .map(
        ({
          id,
          fechaVenta,
          productorId,
          usuarioId,
          productoId,
          estado,
          canalDeVentaId,
          canalDeVentaExternoId,
          productorNivel1Id,
          productorNivel2Id,
          productorNivel3Id,
          productorNivel4Id,
          patente,
          marcaId,
          modeloId,
          anioFabricacion,
          colorId,
          kilometraje,
          cliente,
          facturacion,
          medioDePago,
          numeroTarjeta,
          numeroCBU,
          companiaId,
        }) => ({
          id,
          fechaVenta,
          productorId,
          usuarioId,
          productoId,
          estado,
          canalDeVentaId,
          canalDeVentaExternoId,
          productorNivel1Id,
          productorNivel2Id,
          productorNivel3Id,
          productorNivel4Id,
          patente,
          marcaId,
          modeloId,
          anioFabricacion,
          colorId,
          kilometraje,
          cliente: {
            nombre: cliente.nombre,
            apellido: cliente.apellido,
            tipoDocumentoId: cliente.tipoDocumentoId,
            numeroDocumento: cliente.numeroDocumento,
            fechaNacimiento: cliente.fechaNacimiento,
            localidadId: cliente.localidadId,
            codigoPostal: cliente.codigoPostal,
            direccion: cliente.direccion,
            numeroDireccion: cliente.numeroDireccion,
            departamento: cliente.departamento,
            departamentoPiso: cliente.departamentoPiso,
            telefonoMovil: cliente.telefonoMovil,
            telefonoAlternativo: cliente.telefonoAlternativo,
            email: cliente.email,
          },
          facturacion: {
            apellido: facturacion.apellido,
            nombre: facturacion.nombre,
            tipoDocumentoId: facturacion.tipoDocumentoId,
            numeroDocumento: facturacion.numeroDocumento,
            fechaNacimiento: facturacion.fechaNacimiento,
            localidadId: facturacion.localidadId,
            codigoPostal: facturacion.codigoPostal,
            direccion: facturacion.direccion,
            numeroDireccion: facturacion.numeroDireccion,
            departamento: facturacion.departamento,
            departamentoPiso: facturacion.departamentoPiso,
            telefonoMovil: facturacion.telefonoMovil,
            email: facturacion.email,
          },
          medioDePago,
          numeroTarjeta,
          numeroCBU,
          companiaId,
        })
      );
    setIsLoading(true);

    api
      .postSaleAgreements(salesToProcess)
      .then(() => {
        closePreConfirmDialog();
        toastNotification("success", "Contratos creados correctamente.");
        handleSubmit(filters);
        setSelectedItems([]);
      })
      .catch(() => {
        closePreConfirmDialog();
        toastNotification("error", "Error al crear los contratos.");
      })
      .finally(() => setIsLoading(false));
  };

  const handleSelectRow = ({ target }) => {
    setSelectAll(false);
    if (target.checked) {
      setSelectedItems((prev) => prev.concat([+target.name]));
    } else {
      setSelectedItems((prev) => prev.filter((x) => x !== +target.name));
    }
  };

  const getStatusDescription = (id) => {
    const status = Object.values(SaleStatusEnum).find(
      (status) => status.Id === id
    );
    return status ? status.Description.toUpperCase() : "";
  };

  const loadList = (e) => {
    if (e && e.length >= 3) {
      return api.getSellers(e).then(({ data }) =>
        data.map((item) => ({
          key: item.id,
          value: item.id,
          text: item.nombre,
        }))
      );
    }
  };

  return (
    <>
      <Formik
        initialValues={filters}
        onSubmit={(values) => handleSubmit(values)}>
        {({ values, setFieldValue, handleChange, handleSubmit }) => {
          return (
            <>
              <Paper className="c-grid" elevation={3}>
                <Grid>
                  <Grid className="c-grid-header" container spacing={1}>
                    <Grid item xs={6}>
                      <h2>Procesamiento de Ventas</h2>
                    </Grid>
                  </Grid>

                  <Grid
                    className="c-grid-header-filter c-mt-10"
                    container
                    spacing={1}>
                    <Grid item container lg={9} spacing={1}>
                      <Grid item lg={3} xs={12}>
                        <CDatePicker
                          label="Fecha desde"
                          name="dateFrom"
                          value={values?.dateFrom}
                          changeFunction={(e) => setFieldValue("dateFrom", e)}
                        />
                      </Grid>
                      <Grid item lg={3} xs={12}>
                        <CDatePicker
                          label="Fecha hasta"
                          name="dateTo"
                          value={values?.dateTo}
                          changeFunction={(e) => setFieldValue("dateTo", e)}
                        />
                      </Grid>

                      <Grid item lg={3} xs={12}>
                        <CSearchSelect
                          placeHolder="Vendedor"
                          name="seller"
                          onChange={(e) => setFieldValue("seller", e)}
                          loadOptions={loadList}
                          value={values?.seller}
                          clearable
                        />
                        <FormHelperText>
                          Al menos 3 caracteres para buscar.
                        </FormHelperText>
                      </Grid>

                      <Grid item lg={3} xs={12}>
                        <CTextField
                          label="Tamaño de página"
                          name="pageSize"
                          onBlur={() => {
                            !values?.pageSize
                              ? setFieldValue("pageSize", 1)
                              : values?.pageSize > 100 &&
                                setFieldValue("pageSize", 100);
                          }}
                          onChange={handleChange}
                          value={values?.pageSize}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid item container lg={3} spacing={1}>
                      <Grid item lg={12}>
                        <Button
                          className="c-btn-info c-mt-2"
                          variant="contained"
                          onClick={handleSubmit}
                          startIcon={<SearchIcon />}
                          fullWidth>
                          Buscar
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
              {data && data.length > 0 && (
                <>
                  <Paper className="c-mt-20 c-grid-results" elevation={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TableContainer>
                          <Table className="c-table" size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  <Checkbox
                                    color="primary"
                                    name="selectAll"
                                    checked={selectAll}
                                    onClick={handleSelectAll}
                                  />
                                </TableCell>
                                <TableCell>FECHA</TableCell>
                                <TableCell>NÚMERO</TableCell>
                                <TableCell>VENDEDOR</TableCell>
                                <TableCell>DOCUMENTO</TableCell>
                                <TableCell>ESTADO</TableCell>
                                <TableCell>COMPAÑÍA</TableCell>
                                <TableCell>PRODUCTO</TableCell>
                                <TableCell>CANAL DE VENTA</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {data.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <Checkbox
                                      color="primary"
                                      name={item.id}
                                      checked={selectedItems.includes(item.id)}
                                      onClick={handleSelectRow}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {moment(item.fechaVenta).format(
                                      "DD/MM/YYYY"
                                    )}
                                  </TableCell>
                                  <TableCell>{item.id}</TableCell>
                                  <TableCell>{item.nombreUsuario}</TableCell>
                                  <TableCell>
                                    {item.cliente.numeroDocumento}
                                  </TableCell>
                                  <TableCell>
                                    {getStatusDescription(item.estado)}
                                  </TableCell>
                                  <TableCell>{item.companiaNombre}</TableCell>
                                  <TableCell>{item.nombreProducto}</TableCell>
                                  <TableCell>
                                    {item.canalDeVentaNombre}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        container
                        direction="column"
                        alignItems="center"
                        justifyContent="center">
                        <Pagination
                          className="c-pagination"
                          onChange={handleChangePage}
                          page={currentPage}
                          count={
                            parseInt(quantity / filters.pageSize) ===
                            quantity / filters.pageSize
                              ? quantity / filters.pageSize
                              : parseInt(quantity / filters.pageSize) + 1
                          }
                          shape="rounded"
                        />
                      </Grid>
                    </Grid>
                    <Box
                      style={{
                        marginTop: 20,
                        display: "flex",
                        justifyContent: "center",
                      }}>
                      <Button
                        disabled={!selectedItems.length}
                        className="c-btn-success c-mt-2"
                        onClick={() => setConfirmDialog(true)}>
                        Procesar
                      </Button>
                    </Box>
                  </Paper>

                  <ConfirmDialog
                    open={confirmDialog}
                    message={
                      "¿Desea crear " +
                      (selectedItems.length === 1
                        ? selectedItems.length +
                          " contrato para la venta seleccionada?"
                        : selectedItems.length +
                          " contratos para las ventas seleccionadas?")
                    }
                    cancelAction={closePreConfirmDialog}
                    confirmAction={confirmAgreements}
                  />
                </>
              )}
              {data && data.length === 0 && (
                <Paper className="c-mt-20" elevation={3}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} className="c-text-center">
                      <h5>No se encontraron resultados.</h5>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </>
          );
        }}
      </Formik>
    </>
  );
};
